import {
  elapsedMinutes,
  formatDuration,
  formatTimeOfDay,
  formatTimeValue,
  minutesSinceMidnight,
  parseTimeOfDay,
  timeOfDayFromMinutes,
} from '@vendorops/shared';

/**
 * Reading the times a planner actually types.
 *
 * The wedding hours a planner holds in their head arrive as speech — "four",
 * "six fifteen", "half eleven at night" — and the shapes that survive being
 * typed are `4`, `4pm`, `615`, `6:15 PM`, `18:15`. A field that accepts only
 * `18:15` makes the planner do the conversion, which is the whole reason the
 * native time input felt like work. So: everything parseable is accepted, and
 * nothing is ever rounded. A 6:15 start stays 6:15.
 */

const STEP_MINUTES = 30;
/** How far past the start the end list runs. Long enough for a 10am ceremony
 *  that finishes after midnight, short enough to stay scannable. */
const END_SPAN_MINUTES = 14 * 60;

export interface ParseTimeOptions {
  /**
   * `HH:MM`. When set, an entry that could be read as either AM or PM resolves
   * to whichever reading lands soonest after this time — which is what makes
   * `12` mean midnight in an end field sitting after a 6 PM start, and what
   * keeps the planner from having to spell out a meridiem they never say.
   */
  after?: string;
}

/**
 * A typed time as `HH:MM`, or null when it is not a time yet.
 *
 * Null covers both "still typing" and "not a time at all"; the caller decides
 * what to do about that, and the field it backs keeps showing what was typed
 * rather than snapping it to something the planner did not write.
 */
export function parseSpokenTime(raw: string, { after }: ParseTimeOptions = {}): string | null {
  // `4 p.m.` and `4pm` are the same entry; the periods and spaces carry nothing.
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/([ap])\.?m?\.?$/, '$1');

  const m = /^(\d{1,4})(?:[:.](\d{2}))?([ap])?$/.exec(cleaned);
  if (!m) return null;

  const digits = m[1] ?? '';
  const meridiem = m[3];

  let hour: number;
  let minute: number;
  if (m[2] !== undefined) {
    // `4:30`, `4.30`, `16:30` — the separator settles which part is which.
    if (digits.length > 2) return null;
    hour = Number(digits);
    minute = Number(m[2]);
  } else if (digits.length <= 2) {
    hour = Number(digits);
    minute = 0;
  } else {
    // `615` is a quarter past six, `1630` is half four. Read from the right,
    // because the minutes are always the last two digits.
    hour = Number(digits.slice(0, digits.length - 2));
    minute = Number(digits.slice(-2));
  }
  if (minute > 59) return null;

  if (meridiem === 'a' || meridiem === 'p') {
    // A spelled-out meridiem only makes sense on a 12-hour clock, so `13pm` is
    // a typo rather than 1 PM.
    if (hour > 12 || hour < 1) return null;
    const base = hour % 12;
    return formatTimeValue({ hour: meridiem === 'p' ? base + 12 : base, minute });
  }

  if (hour > 23) return null;
  // 0 and 13–23 can only be the 24-hour clock; there is nothing to resolve.
  if (hour === 0 || hour > 12) return formatTimeValue({ hour, minute });

  const am = formatTimeValue({ hour: hour % 12, minute });
  const pm = formatTimeValue({ hour: (hour % 12) + 12, minute });
  if (after) {
    const amGap = elapsedMinutes(after, am);
    const pmGap = elapsedMinutes(after, pm);
    // A zero gap means the entry landed on the start itself, which is the one
    // reading a planner cannot have meant.
    const candidates = [
      { value: am, gap: amGap },
      { value: pm, gap: pmGap },
    ].filter((c): c is { value: string; gap: number } => c.gap !== null && c.gap > 0);
    if (candidates.length) {
      return candidates.reduce((best, c) => (c.gap < best.gap ? c : best)).value;
    }
  }
  // Weddings happen in the afternoon and evening, so a bare hour is PM. The
  // field shows `4:00 PM` the moment it commits, so a morning ceremony reads
  // wrong immediately and takes two keystrokes — `am` — to correct.
  return pm;
}

export interface TimeOption {
  /** `HH:MM`. */
  value: string;
  /** `4:00 PM`. */
  label: string;
  /** `7 hr` — how long the day runs, on an end option only. */
  detail?: string;
}

/**
 * The half-hours offered in the menu.
 *
 * `after` turns the list into an end-time list: it begins past the start,
 * carries how long each choice makes the day, and runs through midnight, so a
 * reception finishing at 12:30 AM is an ordinary pick rather than a value the
 * planner has to know the field will accept.
 *
 * `current` is spliced in wherever it belongs chronologically when it is not
 * already on the grid. An odd time already chosen — typed here, or arriving
 * from a wedding saved months ago — has to read as a member of this set, not as
 * something the menu has never heard of.
 */
export function timeOptions({ current, after }: { current?: string; after?: string }): TimeOption[] {
  const values: string[] = [];

  if (after) {
    const start = parseTimeOfDay(after);
    if (!start) return [];
    const startMinutes = minutesSinceMidnight(start);
    // Clean clock times, not start-plus-thirty: "out by eleven" is what a
    // planner says, and the duration beside it carries the odd minutes.
    const first = Math.ceil((startMinutes + STEP_MINUTES) / STEP_MINUTES) * STEP_MINUTES;
    for (let m = first; m <= startMinutes + END_SPAN_MINUTES; m += STEP_MINUTES) {
      values.push(formatTimeValue(timeOfDayFromMinutes(m)));
    }
  } else {
    for (let m = 0; m < 1440; m += STEP_MINUTES) {
      values.push(formatTimeValue(timeOfDayFromMinutes(m)));
    }
  }

  if (current && parseTimeOfDay(current) && !values.includes(current)) {
    const index = after
      ? // The end list is ordered by how far each time is from the start, not by
        // clock value, because it crosses midnight partway down.
        values.findIndex((v) => (elapsedMinutes(after, v) ?? 0) > (elapsedMinutes(after, current) ?? 0))
      : values.findIndex((v) => v > current);
    values.splice(index === -1 ? values.length : index, 0, current);
  }

  return values.map((value) => {
    const gap = after ? elapsedMinutes(after, value) : null;
    return {
      value,
      label: formatTimeOfDay(value) ?? value,
      detail: gap ? formatDuration(gap) : undefined,
    };
  });
}
